import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api/axios';
import { CarruselManager } from '@/components/CarruselManager';
import { TestimoniosManager } from '@/components/TestimoniosManager';
import { MetricasSemanal } from '@/components/MetricasSemanal';
import { AntesDespuesManager } from '@/components/AntesDespuesManager';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WhatsappLogo, Funnel, MapPin, ArrowsClockwise, User, Eye, TrendUp, Trophy, LinkSimple, QrCode, Trash } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ESTADOS = [
  { key: 'nuevo', label: 'Nuevo', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  { key: 'contactado', label: 'Contactado', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  { key: 'cotizado', label: 'Cotizado', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  { key: 'ganado', label: 'Ganado', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  { key: 'perdido', label: 'Perdido', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
];

const PRODUCTOS = {
  pasamanos_acero_inox: 'Pasamanos Acero Inox',
  ventanas_puertas_euro: 'Ventanas y Puertas Euro',
  fachadas_vidrio_templado: 'Fachadas Vidrio Templado',
  duchas_mamparas: 'Bañeras, Duchas y Mamparas',
  techo_curvo: 'Techo Curvo',
  pvc_puertas_ventanas: 'PVC Puertas y Ventanas',
  acero_inox_trabajos: 'Acero Inox (Mamparas y Más)',
};

const SHARE_SLUGS = {
  pasamanos_acero_inox: 'pasamanos',
  ventanas_puertas_euro: 'euro',
  fachadas_vidrio_templado: 'fachadas',
  duchas_mamparas: 'duchas',
  techo_curvo: 'techo',
  pvc_puertas_ventanas: 'pvc',
  acero_inox_trabajos: 'aceroinox',
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short' }) + ' ' +
    d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
};

export default function LeadsLanding() {
  const { user, isAdmin } = useAuth();
  const puedeEliminar = isAdmin || user?.role === 'supervisor';
  const puedeTestimonios = isAdmin || ['supervisor', 'community_manager'].includes(user?.role);
  const SIN_SEG_DIAS = 2;
  const esSinSeguimiento = (l) => (l.estado || 'nuevo') === 'nuevo' && (Date.now() - new Date(l.created_at).getTime()) / 86400000 >= SIN_SEG_DIAS;
  const diasSin = (l) => Math.floor((Date.now() - new Date(l.created_at).getTime()) / 86400000);
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [filtro, setFiltro] = useState('');
  const [users, setUsers] = useState([]);
  const [metricas, setMetricas] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await api.get('/landing/leads', { params: filtro && filtro !== 'sin_seguimiento' ? { estado: filtro } : {} });
      setLeads(res.data.leads || []);
      setCounts(res.data.counts || {});
    } catch { toast.error('Error cargando leads'); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { api.post('/landing/leads/notificaciones/leidas').catch(() => {}); }, []);
  useEffect(() => {
    api.get('/landing/leads-usuarios').then(r => setUsers(r.data.users || [])).catch(() => {});
    api.get('/landing/metricas').then(r => setMetricas(r.data.metricas || [])).catch(() => {});
  }, []);

  const updateLead = async (id, changes) => {
    try {
      const res = await api.put(`/landing/leads/${id}`, changes);
      setLeads((ls) => ls.map((l) => (l.id === id ? res.data.lead : l)));
      fetchLeads();
      toast.success('Lead actualizado');
    } catch { toast.error('No se pudo actualizar'); }
  };

  const deleteLead = async (l) => {
    if (!window.confirm(`¿Eliminar el lead de "${l.nombre}" (${l.telefono})? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/landing/leads/${l.id}`);
      setLeads((ls) => ls.filter((x) => x.id !== l.id));
      fetchLeads();
      toast.success('Lead eliminado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const waUrl = (tel, nombre) =>
    `https://wa.me/${tel.replace(/\D/g, '').replace(/^(\d{8})$/, '502$1')}?text=${encodeURIComponent(`¡Hola ${nombre.split(' ')[0]}! Te escribimos de Soluciones Arquitectónicas sobre tu cotización 😊`)}`;

  const copiarLink = (producto) => {
    const link = `${window.location.origin}/api/landing/share/${SHARE_SLUGS[producto]}`;
    navigator.clipboard.writeText(link)
      .then(() => toast.success('Link copiado — listo para pegar en tus campañas', { description: link }))
      .catch(() => toast.error('No se pudo copiar'));
  };

  const descargarQR = (slug, nombre) => {
    const a = document.createElement('a');
    a.href = `${window.location.origin}/api/landing/qr/${slug}`;
    a.download = `QR_${slug}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`QR de ${nombre} descargado`, { description: 'Listo para tarjetas, vallas y cotizaciones impresas' });
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="leads-landing-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Funnel size={26} className="text-amber-400" /> Leads Landing</h1>
          <p className="text-sm opacity-60">Clientes captados desde las páginas de productos</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => descargarQR('soluciones', 'Catálogo Soluciones')} data-testid="qr-catalogo-btn"
            title="QR del catálogo general /soluciones"
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 transition-colors">
            <QrCode size={16} /> QR Catálogo
          </button>
          <button onClick={fetchLeads} data-testid="leads-refresh-btn"
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-white/15 hover:bg-white/5 transition-colors">
            <ArrowsClockwise size={16} /> Actualizar
          </button>
        </div>
      </div>

      {/* Métricas por landing */}
      <MetricasSemanal />
      {metricas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="metricas-landings">
          {metricas.map((m) => (
            <Card key={m.producto} className="p-4" data-testid={`metrica-${m.producto}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-sm">{m.nombre}</p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{m.conversion}% conversión</Badge>
                  <button onClick={() => copiarLink(m.producto)} data-testid={`copiar-link-${m.producto}`}
                    title="Copiar link con vista previa para compartir"
                    className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border border-blue-400/40 text-blue-400 hover:bg-blue-400/10 transition-colors">
                    <LinkSimple size={13} /> Copiar link
                  </button>
                  <button onClick={() => descargarQR(SHARE_SLUGS[m.producto], m.nombre)} data-testid={`qr-${m.producto}`}
                    title="Descargar código QR para impresos (tarjetas, vallas, cotizaciones)"
                    className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 transition-colors">
                    <QrCode size={13} /> QR
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="flex items-center justify-center gap-1 text-lg font-black"><Eye size={16} className="text-blue-400" />{m.visitas}</p>
                  <p className="text-[10px] opacity-50 uppercase">Visitas</p>
                </div>
                <div>
                  <p className="flex items-center justify-center gap-1 text-lg font-black"><Funnel size={16} className="text-amber-400" />{m.leads}</p>
                  <p className="text-[10px] opacity-50 uppercase">Leads</p>
                </div>
                <div>
                  <p className="flex items-center justify-center gap-1 text-lg font-black"><TrendUp size={16} className="text-purple-400" />{m.leads_30d}</p>
                  <p className="text-[10px] opacity-50 uppercase">Leads 30d</p>
                </div>
                <div>
                  <p className="flex items-center justify-center gap-1 text-lg font-black"><Trophy size={16} className="text-emerald-400" />{m.ganados}</p>
                  <p className="text-[10px] opacity-50 uppercase">Ganados</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Contadores / filtros */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFiltro('')} data-testid="filtro-todos"
          className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${!filtro ? 'bg-white/10 border-white/40' : 'border-white/15 opacity-60 hover:opacity-100'}`}>
          Todos ({total})
        </button>
        {ESTADOS.map((e) => (
          <button key={e.key} onClick={() => setFiltro(filtro === e.key ? '' : e.key)} data-testid={`filtro-${e.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${e.color} ${filtro === e.key ? 'ring-2 ring-white/40' : 'opacity-70 hover:opacity-100'}`}>
            {e.label} ({counts[e.key] || 0})
          </button>
        ))}
        <button onClick={() => setFiltro(filtro === 'sin_seguimiento' ? '' : 'sin_seguimiento')} data-testid="filtro-sin-seguimiento"
          className={`px-3 py-1.5 rounded-full text-xs font-black border transition-colors bg-red-500/15 text-red-400 border-red-500/40 ${filtro === 'sin_seguimiento' ? 'ring-2 ring-red-400/60' : 'opacity-80 hover:opacity-100'}`}>
          ⏰ Sin seguimiento ({leads.filter(esSinSeguimiento).length})
        </button>
      </div>

      {loading ? (
        <p className="opacity-60 text-sm py-10 text-center">Cargando…</p>
      ) : (filtro === 'sin_seguimiento' ? leads.filter(esSinSeguimiento) : leads).length === 0 ? (
        <Card className="p-10 text-center opacity-60 text-sm" data-testid="leads-empty">
          {filtro === 'sin_seguimiento' ? '🎉 Ningún lead sin seguimiento. Todos han sido atendidos a tiempo.' : `No hay leads ${filtro ? `en estado "${filtro}"` : 'todavía'}. Comparte tus landing pages para empezar a captar clientes.`}
        </Card>
      ) : (
        <div className="space-y-3">
          {(filtro === 'sin_seguimiento' ? leads.filter(esSinSeguimiento).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)) : leads).map((l) => (
            <Card key={l.id} className={`p-4 ${esSinSeguimiento(l) ? 'border-red-500/40' : ''}`} data-testid={`lead-card-${l.id}`}>
              <div className="flex flex-wrap gap-4 items-start justify-between">
                <div className="min-w-[200px]">
                  <p className="font-bold">{l.nombre}
                    {esSinSeguimiento(l) && (
                      <span data-testid={`lead-sin-seg-${l.id}`} className="ml-2 text-[10px] font-black px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/40">
                        ⏰ {diasSin(l)} días sin seguimiento
                      </span>
                    )}
                  </p>
                  <p className="text-sm opacity-70">{l.telefono}</p>
                  {l.chat_activo && (
                    <span data-testid={`lead-chat-activo-${l.id}`}
                      className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-400/30"
                      title={`Ya tiene conversación en el Inbox (${l.chat_platform || 'chat'})${l.chat_customer_name ? ' como ' + l.chat_customer_name : ''} — evita contactarlo doble`}>
                      💬 Ya tiene chat activo{l.chat_platform ? ` · ${l.chat_platform === 'whatsapp' ? 'WhatsApp' : 'Messenger'}` : ''}
                    </span>
                  )}
                  <p className="text-xs opacity-50 flex items-center gap-1 mt-1"><MapPin size={12} /> {l.departamento} · {fmtFecha(l.created_at)}</p>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <Badge variant="outline" className="text-[10px] mb-1">{PRODUCTOS[l.producto] || l.producto}</Badge>
                  {l.detalles && <p className="text-sm opacity-80">{l.detalles}</p>}
                  {l.nota && <p className="text-xs opacity-60 mt-1 italic">📝 {l.nota}</p>}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <div className="flex gap-2 items-center">
                    <select value={l.estado || 'nuevo'} data-testid={`lead-estado-${l.id}`}
                      onChange={(e) => updateLead(l.id, { estado: e.target.value })}
                      className="text-xs rounded-lg px-2 py-1.5 border border-white/20 bg-transparent outline-none cursor-pointer"
                      style={{ colorScheme: 'dark' }}>
                      {ESTADOS.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                    </select>
                    <a href={waUrl(l.telefono, l.nombre)} target="_blank" rel="noreferrer" data-testid={`lead-wa-${l.id}`}
                      className="bg-[#25D366] hover:brightness-110 text-white p-2 rounded-lg transition-all" title="Abrir WhatsApp">
                      <WhatsappLogo size={18} weight="fill" />
                    </a>
                    {puedeEliminar && (
                      <button onClick={() => deleteLead(l)} data-testid={`lead-delete-${l.id}`}
                        className="border border-red-400/40 text-red-400 hover:bg-red-500/15 p-2 rounded-lg transition-colors" title="Eliminar lead">
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs opacity-70">
                    <User size={12} />
                    <select value={l.asignado_a || ''} data-testid={`lead-asignar-${l.id}`}
                      onChange={(e) => {
                        const u = users.find((x) => x.id === e.target.value);
                        updateLead(l.id, { asignado_a: e.target.value, asignado_nombre: u ? (u.name || u.username) : '' });
                      }}
                      className="text-xs rounded-lg px-2 py-1 border border-white/15 bg-transparent outline-none cursor-pointer max-w-[150px]"
                      style={{ colorScheme: 'dark' }}>
                      <option value="">Sin asignar</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CarruselManager productos={PRODUCTOS} />
      {puedeTestimonios && <TestimoniosManager productos={PRODUCTOS} />}

      <AntesDespuesManager productos={PRODUCTOS} />
    </div>
  );
}
